import { useCallback, useEffect, useState } from 'react';
import Avatar from '../app/components/Avatar';
import { useApp } from '../app/lib/AppContext';
import { Link, useSearchParams } from 'react-router';
import { vnDate } from '../app/lib/coach';
import { ROLES, SIGN_IN, TIERS, TIER_STYLES, planText } from './user/labels';

// Admin panel › Người dùng: every account, newest first, with how they sign in, what they came for and the
// plan they're on. Search by name, phone, email or id; filter by plan. A name opens that account's page
// (admin/user/UserDetailPage), where plans, coach seats, credits and locks are changed.

const PLACEMENTS = { onboarding: 'Cuối onboarding', first_ai: 'Sau món AI đầu tiên', quota: 'Hết lượt AI', paywall: 'Màn Pro, Gói & lượt AI' };

// Where the 7-day trial was offered, and how many people who saw it there started it (distinct people per place).
function TrialFunnel({ rows }) {
  const pct = (a, b) => (b ? `${Math.round((a / b) * 100)}%` : '–');
  return (
    <section className="rounded-[20px] bg-base-100 p-4">
      <h2 className="font-bold">Lời mời dùng thử</h2>
      <p className="text-sm text-muted">Số người được mời ở mỗi chỗ, bấm "Để sau", và bắt đầu dùng thử từ chỗ đó.</p>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted">
            <tr><th className="py-1 pr-2 font-semibold">Chỗ mời</th><th className="px-2 text-right font-semibold">Đã mời</th>
              <th className="px-2 text-right font-semibold">Để sau</th><th className="px-2 text-right font-semibold">Bắt đầu</th>
              <th className="pl-2 text-right font-semibold">Tỉ lệ</th></tr>
          </thead>
          <tbody className="tabular divide-y divide-line">
            {rows.map((r) => (
              <tr key={r.placement}>
                <td className="py-2 pr-2">{PLACEMENTS[r.placement] || r.placement}</td>
                <td className="px-2 text-right">{r.shown}</td>
                <td className="px-2 text-right">{r.dismissed}</td>
                <td className="px-2 text-right">{r.started}</td>
                <td className="pl-2 text-right font-semibold">{pct(r.started, r.shown)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function UserRow({ user: u }) {
  const label = TIERS.find((t) => t.id === u.plan.tier)?.label;
  return (
    <li className="flex items-start gap-3 py-3">
      <span className="shrink-0 overflow-hidden rounded-full"><Avatar name={u.name} url={u.avatar_url} size={40} /></span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Link to={`/admin/users/${u.id}`} className="font-bold underline-offset-2 hover:underline">{u.name || 'Không tên'}</Link>
          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${TIER_STYLES[u.plan.tier]}`}>{label}</span>
          {u.coach && <span className="rounded-full bg-base-200 px-2 py-0.5 text-xs font-bold">HLV · {u.coach.code}</span>}
        </div>
        <p className="text-sm text-muted">
          #{u.id}
          {u.phone && <> · <a className="text-brand-text underline" href={`tel:${u.phone}`}>{u.phone}</a></>}
          {u.email && <> · {u.email}</>}
        </p>
        <p className="text-sm">{planText(u.plan)}</p>
        <p className="text-xs text-muted">
          {[
            u.sign_in.length ? `Đăng nhập: ${u.sign_in.map((s) => SIGN_IN[s] || s).join(', ')}` : null,
            ROLES[u.role],
            !u.onboarded && 'Chưa xong onboarding',
            u.coach && (u.coach.plan_active ? `${u.coach.seats} chỗ đến ${vnDate(u.coach.plan_ends_at)}` : 'Chưa có chỗ'),
            `Tạo ${vnDate(u.created_at)}`,
            u.last_log_on ? `Ghi gần nhất ${vnDate(u.last_log_on)}` : 'Chưa ghi nhật ký',
          ].filter(Boolean).join(' · ')}
        </p>
      </div>
    </li>
  );
}

export default function UsersPage() {
  const { api } = useApp();
  // The search and the plan filter live in the address, so coming back from a user's page keeps them.
  const [params, setParams] = useSearchParams();
  const query = params.get('q') || '';
  const tier = params.get('tier') || '';
  const [q, setQ] = useState(query);
  const setFilter = (next) => setParams(Object.fromEntries(Object.entries({ q: query, tier, ...next }).filter(([, v]) => v)), { replace: true });
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback((more = false) => {
    const search = new URLSearchParams();
    if (query) search.set('q', query);
    if (tier) search.set('tier', tier);
    if (more && data?.users.length) search.set('before', data.users[data.users.length - 1].id);
    return api.get(`/admin/users?${search}`)
      .then((res) => setData((prev) => (more ? { ...res, users: [...prev.users, ...res.users] } : res)))
      .catch((e) => setError(e.message));
  }, [api, query, tier, data]);

  useEffect(() => { setData(null); setError(null); load(); }, [query, tier]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-[22px] font-bold">Người dùng</h1>
        <p className="text-sm text-muted">Mọi tài khoản, mới nhất trước, với gói đang dùng. Tìm theo tên, số điện thoại, email hoặc id.</p>
      </div>
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); setFilter({ q: q.trim() }); }}>
        <input className="input h-12 min-w-0 flex-1 rounded-[14px] border-line text-base" placeholder="Tên, số điện thoại, email, id"
          value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="submit" className="btn h-12 rounded-[14px] border-line bg-base-100 px-5">Tìm</button>
      </form>
      <div role="tablist" className="flex gap-1 overflow-x-auto rounded-[16px] bg-base-300/70 p-1">
        {TIERS.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tier === t.id} onClick={() => setFilter({ tier: t.id })}
            className={`h-10 shrink-0 grow whitespace-nowrap rounded-[12px] px-3 text-sm font-semibold ${tier === t.id ? 'bg-base-100 shadow-sm' : 'text-muted'}`}>
            {t.label}{data?.counts ? ` (${data.counts[t.count || t.id]})` : ''}
          </button>
        ))}
      </div>

      {data?.trial_funnel && <TrialFunnel rows={data.trial_funnel} />}
      {error && <div role="alert" className="rounded-[20px] bg-[#fdebe4] p-4 text-sm text-[#8e3414]">{error}</div>}
      {!data && !error && <div className="skeleton h-64 rounded-[20px]" />}
      {data?.users.length === 0 && <p className="rounded-[20px] border border-dashed border-line p-5 text-center text-sm text-muted">Không có ai.</p>}
      {data?.users.length > 0 && (
        <ul className="divide-y divide-line rounded-[20px] bg-base-100 px-4">
          {data.users.map((u) => <UserRow key={u.id} user={u} />)}
        </ul>
      )}
      {data?.more && <button type="button" className="btn h-11 rounded-[14px] border-line bg-base-100" onClick={() => load(true)}>Xem thêm</button>}
    </div>
  );
}
